import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import api from '../utils/api';
import useAuthStore from '../store/useAuthStore';
import ProductCard from '../components/ProductCard';
import { FiHeart } from 'react-icons/fi';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger } from '../components/ui';

export default function WishlistPage() {
  const { user } = useAuthStore();

  const { data, isLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: () => api.get('/wishlist'),
    enabled: !!user,
  });

  if (!user) return <EmptyState title="Sign in to see your wishlist" body="Heart the pieces you love and find them again later." cta={{ to: '/login', label: 'Sign in' }} />;

  const wishlist = data?.wishlist || [];

  if (isLoading) return <div style={{ textAlign: 'center', padding: 120 }}>Loading…</div>;

  if (wishlist.length === 0) return <EmptyState title="Your wishlist is empty" body="Heart the pieces you love — find them right here." cta={{ to: '/', label: 'Browse the shop' }} />;

  return (
    <div>
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '52px 24px 44px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1280, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <GlassChip>Saved for later</GlassChip>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="serif" style={{ fontSize: 'clamp(34px, 4.4vw, 52px)', color: '#FFF', lineHeight: 1.05, letterSpacing: '-0.02em', marginTop: 16 }}>
            Wishlist <span style={{ color: 'var(--gold)', fontSize: '0.55em', marginLeft: 12 }}>{wishlist.length}</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} style={{ fontSize: 13, color: 'rgba(237,227,209,0.6)', marginTop: 8 }}>
            {wishlist.length} saved {wishlist.length === 1 ? 'piece' : 'pieces'}
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 24px 64px' }}>
        <motion.div variants={stagger} initial="hidden" animate="show" className="wishlist-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 22 }}>
          {wishlist.map(p => (
            <motion.div key={p._id} variants={fadeUp} whileHover={{ y: -6 }} transition={{ duration: 0.25 }}>
              <ProductCard product={p} />
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style>{`
        @media (max-width: 900px) { .wishlist-grid { grid-template-columns: repeat(2, 1fr) !important; } }
      `}</style>
    </div>
  );
}

function EmptyState({ title, body, cta }) {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px' }}>
      <Reveal style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ display: 'inline-flex', width: 80, height: 80, borderRadius: '50%', background: 'var(--primary-soft)', color: 'var(--primary)', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <FiHeart size={36} />
        </div>
        <h2 className="serif" style={{ fontSize: 32, marginBottom: 10 }}>{title}</h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.7 }}>{body}</p>
        <MagneticButton to={cta.to} variant="primary">{cta.label}</MagneticButton>
      </Reveal>
    </div>
  );
}
